"use client";

import { ChevronDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { cn, padNumber } from "@/lib/utils";

// =============================================================================

type TocItem = { id: string; title: string };

/**
 * Contents of a legal page: sticky beside the sections on large screens,
 * folded above them on small ones. The section crossing the upper part of the
 * screen is highlighted (Lenis scrolls to the anchors, header offset
 * included).
 */
export function LegalToc({
  items,
  label,
}: {
  items: TocItem[];
  label: string;
}) {
  const [activeId, setActiveId] = useState(items[0]?.id);
  const detailsRef = useRef<HTMLDetailsElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.find((entry) => entry.isIntersecting);
        if (visible) setActiveId(visible.target.id);
      },
      { rootMargin: "-20% 0px -70% 0px" }
    );
    for (const { id } of items) {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    }
    return () => observer.disconnect();
  }, [items]);

  const links = (
    <ol className="space-y-0.5">
      {items.map(({ id, title }, index) => {
        const isActive = id === activeId;
        return (
          <li key={id}>
            <a
              href={`#${id}`}
              onClick={() => {
                setActiveId(id);
                if (detailsRef.current) detailsRef.current.open = false;
              }}
              aria-current={isActive ? "location" : undefined}
              className={cn(
                "group flex items-baseline gap-3 rounded-lg px-3 py-2 text-[0.95rem] leading-snug transition-colors duration-300",
                isActive
                  ? "bg-surface-2 text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              <span
                className={cn(
                  "font-mono text-xs tabular-nums transition-colors duration-300",
                  isActive ? "text-brand" : "text-faded"
                )}
              >
                {padNumber(index + 1)}
              </span>
              {title}
            </a>
          </li>
        );
      })}
    </ol>
  );

  return (
    <>
      <aside className="hidden lg:block">
        <nav aria-label={label} className="sticky top-28">
          <p className="px-3 text-xs font-semibold tracking-[0.14em] text-muted-foreground uppercase">
            {label}
          </p>
          <div className="mt-4">{links}</div>
        </nav>
      </aside>

      <details
        ref={detailsRef}
        className="group/toc rounded-xl border border-foreground/10 bg-surface-1 lg:hidden"
      >
        <summary className="flex cursor-pointer list-none items-center justify-between px-4 py-3.5 font-medium [&::-webkit-details-marker]:hidden">
          {label}
          <ChevronDown className="size-4 text-muted-foreground transition-transform duration-300 group-open/toc:rotate-180" />
        </summary>
        <nav aria-label={label} className="border-t border-line p-2">
          {links}
        </nav>
      </details>
    </>
  );
}

// =============================================================================
